import React from 'react';
import {
  Box, HStack, Text, Flex, IconButton, useBreakpointValue, Image,
  Menu, MenuButton, MenuList, MenuItem, MenuDivider, Avatar, Select,
} from '@chakra-ui/react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { FaBars } from 'react-icons/fa';
import { useProjectContext } from '../context/ProjectContext';
import { useAuth } from '../context/AuthContext';
import RoleBadge from './ui/RoleBadge';

export default function Header({ onOpenMenu }) {
  const isMobile = useBreakpointValue({ base: true, md: false });
  const navigate = useNavigate();
  const { projects, loading, selectedProject, setSelectedProject } = useProjectContext();
  const { user, org, role, logout } = useAuth();

  const signOut = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <Flex
      as="header"
      w="100%"
      bg="white"
      borderBottomWidth={1}
      borderColor="gray.200"
      px={{ base: 4, md: 6 }}
      py={3}
      position="fixed"
      top={0}
      left={0}
      zIndex={110}
      align="center"
      gap={3}
      minH="64px"
    >
      <HStack spacing={2.5} flex="1" minW={0}>
        <Box boxSize={8} flexShrink={0} borderRadius="lg" bg="brand.500" display="flex" alignItems="center" justifyContent="center">
          <Image src="/logo512.png" alt="WP QA Testing Dashboard" objectFit="contain" w="70%" h="70%" />
        </Box>
        {!isMobile && (
          <Text fontWeight="bold" fontSize="15px" color="gray.900" letterSpacing="-0.01em" noOfLines={1}>
            WP QA Testing Dashboard
          </Text>
        )}
      </HStack>

      <HStack spacing={2.5} flexShrink={0}>
        {!isMobile && (
          <>
            <Box
              display="inline-flex"
              alignItems="center"
              gap={2}
              h="34px"
              px={3}
              flexShrink={0}
              whiteSpace="nowrap"
              border="1px solid"
              borderColor="gray.200"
              borderRadius="full"
              fontSize="13px"
              fontWeight="500"
              color="gray.700"
            >
              <Box as="svg" viewBox="0 0 24 24" width="14px" height="14px" fill="none" stroke="currentColor" strokeWidth="1.9" color="gray.400" flexShrink={0}>
                <path d="M3 21h18" strokeLinecap="round" />
                <path d="M5 21V7l8-4v18" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M19 21V11l-6-4" strokeLinecap="round" strokeLinejoin="round" />
              </Box>
              <Text as="span">{org?.name || 'Acme QA'}</Text>
            </Box>

            <Select
              w="170px"
              flexShrink={0}
              size="sm"
              bg="white"
              borderColor="gray.200"
              borderRadius="full"
              aria-label="Project selector"
              value={selectedProject || 'ALL PROJECTS'}
              onChange={(e) => setSelectedProject(e.target.value)}
              isDisabled={loading}
            >
              <option value="ALL PROJECTS">All Projects</option>
              {projects.map((p) => (
                <option key={p.id || p} value={p.id || p}>{p.name || p}</option>
              ))}
            </Select>

            <RoleBadge role={role || 'developer'} flexShrink={0} />
          </>
        )}

        {!isMobile ? (
          <Menu>
            <MenuButton aria-label="Account menu" borderRadius="full" flexShrink={0} _focusVisible={{ boxShadow: 'outline' }}>
              <Avatar
                name={user?.name || 'User'}
                src={user?.avatarUrl}
                size="sm"
                bg={user?.avatarUrl ? undefined : 'brand.500'}
                color="white"
              />
            </MenuButton>
            <MenuList>
              <MenuItem as={RouterLink} to="/profile">Profile</MenuItem>
              <MenuItem as={RouterLink} to="/settings">Settings</MenuItem>
              <MenuDivider />
              <MenuItem onClick={signOut}>Sign out</MenuItem>
            </MenuList>
          </Menu>
        ) : (
          <IconButton icon={<FaBars />} aria-label="Open menu" variant="ghost" borderRadius="md" onClick={onOpenMenu} />
        )}
      </HStack>
    </Flex>
  );
}
